/**
 * Shared animation constants for game board components
 *
 * Centralizes animation configurations to prevent recreation on every render
 * and ensures consistency across components.
 */

import type { Easing } from 'framer-motion';
import { DICE_CONFIG } from '../dice/basics/utils/utils';

/**
 * Bounce animation for pawns and dice
 * Used to indicate interactive elements or elements waiting for user action
 */
export const BOUNCE_ANIMATION = {
  y: [0, -5, 0],
  transition: {
    duration: 0.5,
    ease: 'easeInOut' as Easing,
    repeat: Infinity,
    repeatType: 'loop' as const
  }
};

/**
 * Highly visible animation for clickable pawns
 * Combines bounce, scale pulse, and shadow effect to make clickable pawns very noticeable
 */
export const CLICKABLE_PAWN_ANIMATION = {
  y: [0, -12, 0, -12, 0],
  scale: [1, 1.15, 1, 1.15, 1],
  filter: [
    'drop-shadow(0 0 0px rgba(255, 255, 255, 0))',
    'drop-shadow(0 0 8px rgba(255, 215, 0, 0.8))',
    'drop-shadow(0 0 0px rgba(255, 255, 255, 0))',
    'drop-shadow(0 0 8px rgba(255, 215, 0, 0.8))',
    'drop-shadow(0 0 0px rgba(255, 255, 255, 0))'
  ],
  transition: {
    duration: 1.2,
    ease: 'easeInOut' as Easing,
    repeat: Infinity,
    repeatType: 'loop' as const
  }
};

/**
 * Dice bounce animation (slightly higher bounce)
 */
export const DICE_BOUNCE_ANIMATION = {
  y: [0, -10, 0],
  transition: {
    duration: 0.6,
    ease: 'easeInOut' as Easing,
    repeat: Infinity,
    repeatType: 'loop' as const
  }
};

/**
 * Duration of the pawn release-from-base animation in milliseconds.
 * Also drives the store cleanup timer that clears the pawn from the animating
 * set, so the two stay in sync — see {@link createPawnReleaseAnimation}.
 */
export const PAWN_JUMP_DURATION_MS = 900;

/**
 * Release-from-base animation: a springy "pop out of the base" — anticipation
 * squash → high overshoot leap → settle — plus a **full 360° flip** while
 * airborne (landing upright), so a released pawn reads as a real launch and is
 * clearly distinct from an ordinary board move.
 *
 * Transform-only (scale/y/rotate) so it stays GPU-composited.
 */
export const createPawnReleaseAnimation = () => ({
  scale: [1, 0.85, 1.25, 0.95, 1],
  y: [0, 8, -55, 6, 0],
  // Anticipation tilt-back, then a full 360° flip while popping out of the base,
  // landing upright (360 ≡ 0) — the "quick flip" that gives the release character.
  rotate: [0, -20, 160, 340, 360],
  transition: {
    duration: PAWN_JUMP_DURATION_MS / 1000, // Convert to seconds for Framer Motion
    ease: [0.25, 0.46, 0.45, 0.94] as Easing, // easeInOutCubic — arc, not linear
    times: [0, 0.18, 0.5, 0.78, 1]
  }
});

/**
 * Ordinary board-move pop: the same springy squash → overshoot → settle as the
 * release, but with only a **subtle tilt-lean** instead of a full flip — so a
 * normal cell-to-cell move has life without stealing the release's flip.
 */
export const createPawnMovePopAnimation = () => ({
  scale: [1, 0.85, 1.25, 0.95, 1],
  y: [0, 8, -55, 6, 0],
  rotate: [0, -6, -16, 6, 0], // subtle tilt-lean, not a full spin
  transition: {
    duration: PAWN_JUMP_DURATION_MS / 1000,
    ease: [0.25, 0.46, 0.45, 0.94] as Easing,
    times: [0, 0.18, 0.5, 0.78, 1]
  }
});

/**
 * 3D dice roll animation
 * Creates realistic tumbling effect with multi-axis rotation and lift
 * @param duration - Animation duration in milliseconds
 */
export const createDiceRoll3DAnimation = (duration: number) => ({
  rotateX: DICE_CONFIG.ANIMATION_3D.ROTATION_X,
  rotateY: DICE_CONFIG.ANIMATION_3D.ROTATION_Y,
  rotateZ: DICE_CONFIG.ANIMATION_3D.ROTATION_Z,
  scale: DICE_CONFIG.ANIMATION_3D.SCALE,
  y: DICE_CONFIG.ANIMATION_3D.LIFT_Y,
  transition: {
    duration: duration / 1000,
    ease: DICE_CONFIG.ANIMATION_3D.EASING as unknown as Easing[]
  }
});

/**
 * Speed presets for pawn movement animation (in milliseconds per position hop)
 */
export const PAWN_MOVE_DURATION_SLOW_MS = 400;
export const PAWN_MOVE_DURATION_MEDIUM_MS = 250;
export const PAWN_MOVE_DURATION_FAST_MS = 150;

/**
 * Default speed for pawn movement
 */
export const PAWN_MOVE_DURATION_DEFAULT_MS = PAWN_MOVE_DURATION_MEDIUM_MS;

/**
 * Clean jump animation for pawns moving between positions on the board
 * Creates a smooth arc trajectory simulating a jump from case to case
 * @param durationMs - Duration of the hop in milliseconds (default: 250ms)
 */
export const createPawnMoveHopAnimation = (durationMs: number = PAWN_MOVE_DURATION_DEFAULT_MS) => ({
  // Smooth parabolic arc - jumps up and comes back down
  y: [0, -25, 0],

  // Slight scale change for visual feedback
  scale: [1, 1.08, 1],

  transition: {
    duration: durationMs / 1000,
    ease: [0.25, 0.46, 0.45, 0.94] as Easing, // Smooth easeInOutCubic
    times: [0, 0.5, 1] // Peak at midpoint
  }
});

/**
 * Duration of pawn kick animation when capturing opponent
 * Quick, punchy animation to simulate the impact moment
 */
export const PAWN_KICK_DURATION_MS = 250;

/**
 * Settle delay between the attacker pawn arriving at the capture square and
 * the kick animation + tackle SFX firing. Lets the bounce-on-arrival
 * animation visibly complete so the user perceives the attacker as landed
 * before the capture sequence begins (BUG-20260526-05 — issue #809 bug #7).
 */
export const PAWN_CAPTURE_SETTLE_DELAY_MS = 200;

/**
 * Interval between dice face changes during rolling animation
 * Faster interval = more realistic tumbling effect
 *
 * @example
 * ```ts
 * // With 80ms interval and 1000ms animation:
 * // - Faces cycle rapidly for ~850ms (10-11 face changes)
 * // - Settles on final value for last 150ms
 * ```
 */
export const DICE_FACE_CYCLE_INTERVAL_MS = 80;

/**
 * Time before animation ends to settle on final value
 * Allows the last face to be visible before animation completes
 *
 * @example
 * ```ts
 * // For 1000ms animation duration:
 * // - Cycle phase: 0ms to 850ms (faces change rapidly)
 * // - Settle phase: 850ms to 1000ms (shows final value)
 * ```
 */
export const DICE_SETTLE_BEFORE_END_MS = 150;

/**
 * Duration of pawn rollback animation per hop when captured
 * Very fast to simulate being "kicked back" with speed
 */
export const PAWN_ROLLBACK_DURATION_MS = 50;

/**
 * Duration of pawn final celebration animation in milliseconds
 * One-shot animation played when pawn reaches XFINAL position
 */
export const PAWN_FINAL_CELEBRATION_DURATION_MS = 800;

/**
 * Kick animation for capturing pawn
 * Creates a dramatic, highly visible impact effect to show the capture moment
 * @param durationMs - Duration of the kick in milliseconds (default: 250ms)
 */
export const createPawnKickAnimation = (durationMs: number = PAWN_KICK_DURATION_MS) => ({
  // Dramatic kick motion - strong forward lunge and back
  x: [0, 40, 0],

  // Strong scale pulse for dramatic impact
  scale: [1, 1.4, 1],

  // Add slight rotation for more dynamic effect
  rotate: [0, 5, 0],

  transition: {
    duration: durationMs / 1000,
    ease: [0.6, 0.01, 0.05, 0.9] as Easing, // Sharp easing for powerful kick
    times: [0, 0.5, 1] // Peak at midpoint
  }
});

/**
 * Victim reaction animation when being kicked
 * Creates a visible recoil/shake effect before rollback
 * @param durationMs - Duration of the reaction in milliseconds (default: 250ms)
 */
export const createVictimReactionAnimation = (durationMs: number = PAWN_KICK_DURATION_MS) => ({
  // Recoil motion - shake and scale down from impact
  x: [0, -15, 10, -5, 0],

  // Scale down from impact
  scale: [1, 0.9, 0.85, 0.9, 1],

  // Slight rotation shake
  rotate: [0, -3, 2, -1, 0],

  transition: {
    duration: durationMs / 1000,
    ease: [0.6, 0.01, 0.05, 0.9] as Easing,
    times: [0, 0.3, 0.6, 0.8, 1]
  }
});

/**
 * Rollback slide animation for a captured pawn returning to base.
 * Reads as a knock-back: a quick recoil dip on impact, then an eased vertical
 * arc as the pawn is flung back toward its base — distinct from normal movement.
 * @param durationMs - Duration per hop in milliseconds (default: 50ms)
 */
export const createPawnRollbackSlideAnimation = (
  durationMs: number = PAWN_ROLLBACK_DURATION_MS
) => ({
  // Stays fully opaque throughout the return.
  opacity: 1,

  // Recoil dip on impact, then settle — the knock-back read.
  scale: [1, 0.88, 1],

  // Eased vertical arc back toward base (replaces the old flat slide).
  y: [0, -12, 0],

  transition: {
    duration: durationMs / 1000,
    ease: [0.25, 0.46, 0.45, 0.94] as Easing, // easeInOutCubic — arc, not linear
    times: [0, 0.4, 1] // Recoil peak partway, then settle
  }
});

/**
 * Celebration animation for pawn reaching XFINAL position
 * One-shot animation with scale pulse and golden glow effect
 * Similar style to CLICKABLE_PAWN_ANIMATION but plays only once
 */
export const PAWN_FINAL_CELEBRATION_ANIMATION = {
  // Dramatic scale pulse up and back to normal
  scale: [1, 1.3, 1.1, 1.25, 1],

  // Vertical bounce for celebration
  y: [0, -20, -5, -15, 0],

  // Golden glow effect that fades in and out
  filter: [
    'drop-shadow(0 0 0px rgba(255, 215, 0, 0))',
    'drop-shadow(0 0 15px rgba(255, 215, 0, 1))',
    'drop-shadow(0 0 8px rgba(255, 215, 0, 0.7))',
    'drop-shadow(0 0 12px rgba(255, 215, 0, 0.9))',
    'drop-shadow(0 0 0px rgba(255, 215, 0, 0))'
  ],

  transition: {
    duration: PAWN_FINAL_CELEBRATION_DURATION_MS / 1000,
    ease: [0.25, 0.46, 0.45, 0.94] as Easing, // Smooth easeInOutCubic
    times: [0, 0.25, 0.5, 0.75, 1]
  }
};

/**
 * Create celebration animation for reaching XFINAL
 * Factory function to allow customizing duration if needed
 * @param durationMs - Duration in milliseconds (default: 800ms)
 */
export const createPawnFinalCelebrationAnimation = (
  durationMs: number = PAWN_FINAL_CELEBRATION_DURATION_MS
) => ({
  ...PAWN_FINAL_CELEBRATION_ANIMATION,
  transition: {
    ...PAWN_FINAL_CELEBRATION_ANIMATION.transition,
    duration: durationMs / 1000
  }
});

/**
 * Duration of the dramatic "pawn reached home" one-shot celebration.
 */
export const PAWN_HOME_CELEBRATION_DURATION_MS = 1300;

/**
 * Dramatic one-shot celebration when a pawn settles on its final home cell.
 * Bigger, bouncier, and brighter than {@link PAWN_FINAL_CELEBRATION_ANIMATION}
 * (a hop-pulse) so the arrival really lands: a triple over-scale bounce, a
 * playful rotate wiggle, and a strong pulsing golden glow. Transform/opacity
 * (filter) only — GPU-composited. Used as a stable reference so it fires once
 * on arrival and never replays on re-render.
 */
export const PAWN_HOME_CELEBRATION_ANIMATION = {
  scale: [1, 1.55, 0.92, 1.4, 1.05, 1.2, 1],
  y: [0, -38, -6, -30, -4, -16, 0],
  rotate: [0, -10, 9, -7, 5, -2, 0],
  filter: [
    'drop-shadow(0 0 0px rgba(255, 215, 0, 0))',
    'drop-shadow(0 0 24px rgba(255, 215, 0, 1))',
    'drop-shadow(0 0 12px rgba(255, 215, 0, 0.85))',
    'drop-shadow(0 0 28px rgba(255, 215, 0, 1))',
    'drop-shadow(0 0 10px rgba(255, 215, 0, 0.75))',
    'drop-shadow(0 0 20px rgba(255, 215, 0, 0.95))',
    'drop-shadow(0 0 0px rgba(255, 215, 0, 0))'
  ],
  transition: {
    duration: PAWN_HOME_CELEBRATION_DURATION_MS / 1000,
    ease: [0.25, 0.46, 0.45, 0.94] as Easing, // easeInOutCubic
    times: [0, 0.15, 0.3, 0.45, 0.6, 0.8, 1]
  }
};

/**
 * How long the win-celebration overlay stays up before it auto-dismisses.
 * The confetti particle durations are tuned to finish within this window.
 */
export const WIN_CELEBRATION_DURATION_MS = 4000;

/** Palette the confetti burst draws from (the four player colours + white). */
const CONFETTI_COLORS = ['#22c55e', '#eab308', '#3b82f6', '#ef4444', '#ffffff'] as const;

/** Descriptor for a single confetti particle (transform/opacity only). */
export interface ConfettiParticle {
  id: number;
  /** Horizontal start position as a viewport-width percentage. */
  leftPct: number;
  /** Stagger before the particle starts falling, in milliseconds. */
  delayMs: number;
  /** Fall duration, in milliseconds. */
  durationMs: number;
  /** Horizontal drift while falling, in pixels. */
  driftPx: number;
  /** Total rotation over the fall, in degrees. */
  rotateDeg: number;
  /** Fill colour. */
  color: string;
}

/**
 * Builds a deterministic set of confetti particle descriptors. Values are
 * derived from the index (no `Math.random`) so the burst is stable across
 * re-renders and Storybook snapshots. Every particle animates only
 * `transform`/`opacity`, so the burst stays GPU-composited.
 *
 * @param count - Number of particles (from `MOTION_PROFILE.confettiCount`).
 */
export const buildConfettiParticles = (count: number): ConfettiParticle[] =>
  Array.from({ length: Math.max(0, count) }, (_, i) => {
    const a = ((i * 2654435761) % 1000) / 1000; // fractional hash in [0, 1)
    const b = ((i * 40503) % 997) / 997; // second, decorrelated hash
    return {
      id: i,
      leftPct: Math.round(a * 100),
      delayMs: Math.round(b * 400),
      durationMs: 1600 + Math.round(a * 1200),
      driftPx: Math.round((b - 0.5) * 120),
      rotateDeg: Math.round(a * 720) - 360,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]
    };
  });
